import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, NumberInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Package, Plus, Trash2, Search, AlertTriangle, Check, X } from "lucide-react";
import { fmtINR, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import TID from "@/constants/testIds";

const STATUS_TONE = {
    PENDING: { tone: "amber", label: "Pending" },
    ACCEPTED: { tone: "emerald", label: "Accepted" },
    REJECTED: { tone: "red", label: "Rejected" },
};

const emptyForm = {
    supplier_id: "", zbc_id: null, part_number: "", part_name: "",
    quote_amount: "", quoted_at: new Date().toISOString().slice(0, 10), valid_until: "",
    notes: "",
};

export default function SupplierQuotes() {
    const { user } = useAuth();
    const canEdit = hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING", "PURCHASE_MANAGER");
    const canDelete = hasRole(user, "CORPORATE_ADMIN");

    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [suppliers, setSuppliers] = useState([]);
    const [filterSupplier, setFilterSupplier] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterStale, setFilterStale] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    // Inline ZBC search-to-link, reusing the same read-only search endpoint
    // the Manual ZBC form's "Clone from Existing ZBC" feature uses.
    const [zbcQuery, setZbcQuery] = useState("");
    const [zbcResults, setZbcResults] = useState([]);
    const [zbcSearching, setZbcSearching] = useState(false);
    const [linkedZbc, setLinkedZbc] = useState(null);

    const load = () => {
        const params = {};
        if (filterSupplier) params.supplier_id = filterSupplier;
        if (filterStatus) params.status = filterStatus;
        if (filterStale) params.stale = true;
        api.get("/supplier-quotes", { params })
            .then((r) => setRows(r.data))
            .catch((e) => setError(formatApiError(e)));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterSupplier, filterStatus, filterStale]);
    useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

    const openCreate = () => {
        setForm(emptyForm);
        setLinkedZbc(null);
        setZbcQuery(""); setZbcResults([]);
        setModalOpen(true);
    };

    const searchZbc = async () => {
        setZbcSearching(true);
        try {
            const { data } = await api.get(`/zbc/ai/catalog?q=${encodeURIComponent(zbcQuery)}&limit=15`);
            setZbcResults(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setZbcSearching(false); }
    };

    const pickZbc = (z) => {
        setLinkedZbc(z);
        setForm((f) => ({ ...f, zbc_id: z.id, part_number: z.part_number, part_name: z.part_name }));
        setZbcResults([]);
    };

    const clearZbcLink = () => {
        setLinkedZbc(null);
        setForm((f) => ({ ...f, zbc_id: null }));
    };

    const submit = async () => {
        if (!form.supplier_id) return toast.error("Pick a supplier");
        if (!form.part_number.trim()) return toast.error("Part number is required");
        if (!form.quote_amount || parseFloat(form.quote_amount) <= 0) return toast.error("Enter a quote amount");
        setSaving(true);
        try {
            await api.post("/supplier-quotes", {
                supplier_id: form.supplier_id,
                zbc_id: form.zbc_id || null,
                part_number: form.part_number.trim(),
                part_name: form.part_name?.trim() || null,
                quote_amount: parseFloat(form.quote_amount),
                quoted_at: new Date(form.quoted_at).toISOString(),
                valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
                notes: form.notes?.trim() || null,
            });
            toast.success("Quote logged");
            setModalOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const setStatus = async (row, status) => {
        try {
            await api.put(`/supplier-quotes/${row.id}/status`, { status });
            toast.success(`Marked ${status.toLowerCase()}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete the ${fmtINR(row.quote_amount)} quote from ${row.supplier_name} for ${row.part_number}?`)) return;
        try { await api.delete(`/supplier-quotes/${row.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const isStale = (row) => {
        const days = (Date.now() - new Date(row.quoted_at).getTime()) / 86400000;
        return days > 180;
    };

    return (
        <div data-testid={TID.supplierQuotesPage}>
            <PageHeader
                eyebrow="MODULE"
                title="Supplier Quotes"
                subtitle="Every quote received from a supplier, in one place, with a status and a should-cost comparison when it's linked to a ZBC. Quotes older than 180 days are flagged stale."
                actions={canEdit && (
                    <CopperButton onClick={openCreate} data-testid="sq-add-btn">
                        <Plus className="h-4 w-4" /> Log Quote
                    </CopperButton>
                )}
            />

            <Card className="mb-4 p-3">
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={filterSupplier} onChange={(e) => setFilterSupplier(e.target.value)} className="h-8 text-[12px] max-w-xs">
                        <option value="">All suppliers</option>
                        {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </Select>
                    <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-8 text-[12px] max-w-[160px]">
                        <option value="">All statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="ACCEPTED">Accepted</option>
                        <option value="REJECTED">Rejected</option>
                    </Select>
                    <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={filterStale} onChange={(e) => setFilterStale(e.target.checked)} />
                        Stale only (&gt;180 days)
                    </label>
                </div>
            </Card>

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}

            {rows && rows.length === 0 && (
                <EmptyState
                    icon={Package}
                    title="No supplier quotes logged yet"
                    description={canEdit ? "Log a quote as soon as a supplier sends one, whether or not it's tied to a specific ZBC." : "Awaiting Purchase to log the first quote."}
                    action={canEdit && <CopperButton onClick={openCreate}><Plus className="h-4 w-4" /> Log First Quote</CopperButton>}
                />
            )}

            {rows && rows.length > 0 && (
                <Card>
                    <table className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-3">Supplier</th>
                                <th className="eyebrow text-left py-2 px-3">Part</th>
                                <th className="eyebrow text-right py-2 px-3">Quote</th>
                                <th className="eyebrow text-right py-2 px-3">vs Should-Cost</th>
                                <th className="eyebrow text-left py-2 px-3">Quoted</th>
                                <th className="eyebrow text-left py-2 px-3">Status</th>
                                <th className="eyebrow text-right py-2 px-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const st = STATUS_TONE[r.status] || STATUS_TONE.PENDING;
                                const stale = isStale(r);
                                return (
                                    <tr key={r.id} className="border-b border-slate-100" data-testid={`sq-row-${r.id}`}>
                                        <td className="py-1.5 px-3 text-[13px] font-semibold text-slate-900">{r.supplier_name}</td>
                                        <td className="py-1.5 px-3 text-[13px]">
                                            <div className="font-mono font-semibold">{r.part_number}</div>
                                            {r.part_name && <div className="text-[11px] text-slate-500">{r.part_name}</div>}
                                        </td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px] font-semibold">{fmtINR(r.quote_amount)}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[12px]">
                                            {r.should_cost ? (
                                                <span className={r.variance_pct > 15 ? "text-red-600" : r.variance_pct > 5 ? "text-amber-600" : "text-emerald-600"}>
                                                    {r.variance_pct >= 0 ? "+" : ""}{r.variance_pct}%
                                                </span>
                                            ) : <span className="text-slate-400">—</span>}
                                        </td>
                                        <td className="py-1.5 px-3 text-[12px]">
                                            <div className="flex items-center gap-1.5">
                                                {fmtDate(r.quoted_at)}
                                                {stale && (
                                                    <span title="Older than 180 days" className="inline-flex items-center gap-0.5 text-[10px] text-amber-700">
                                                        <AlertTriangle className="h-3 w-3" /> stale
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-1.5 px-3">
                                            <Pill tone={st.tone}>{st.label}</Pill>
                                        </td>
                                        <td className="py-1.5 px-3 text-right">
                                            <div className="inline-flex gap-1">
                                                {canEdit && r.status === "PENDING" && (
                                                    <>
                                                        <SlateButton onClick={() => setStatus(r, "ACCEPTED")} className="h-7 px-2 text-[11px] text-emerald-700 hover:bg-emerald-50">
                                                            <Check className="h-3 w-3" /> Accept
                                                        </SlateButton>
                                                        <SlateButton onClick={() => setStatus(r, "REJECTED")} className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50">
                                                            <X className="h-3 w-3" /> Reject
                                                        </SlateButton>
                                                    </>
                                                )}
                                                {canDelete && (
                                                    <SlateButton onClick={() => remove(r)} variant="ghost" className="h-7 w-7 px-0 text-red-600 hover:bg-red-50" title="Delete">
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </SlateButton>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}

            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Log Supplier Quote"
                size="lg"
                footer={
                    <>
                        <SlateButton variant="outline" onClick={() => setModalOpen(false)}>Cancel</SlateButton>
                        <CopperButton onClick={submit} disabled={saving} data-testid="sq-save-btn">
                            {saving ? "Saving…" : "Save Quote"}
                        </CopperButton>
                    </>
                }
            >
                <div className="space-y-4">
                    <Field label="Supplier" required>
                        <Select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} data-testid="sq-supplier-select">
                            <option value="">Select supplier…</option>
                            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}
                        </Select>
                    </Field>

                    <div>
                        <div className="eyebrow text-slate-500 mb-1.5">LINK TO A ZBC (OPTIONAL, FOR A SHOULD-COST COMPARISON)</div>
                        {linkedZbc ? (
                            <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 flex items-center justify-between">
                                <div className="text-[12.5px]">
                                    <span className="font-mono font-semibold">{linkedZbc.part_number}</span> — {linkedZbc.part_name}
                                    <span className="text-slate-500 ml-2">Should-Cost {fmtINR(linkedZbc.should_cost || 0)}</span>
                                </div>
                                <SlateButton onClick={clearZbcLink} variant="ghost" className="h-7 text-[11px]">Unlink</SlateButton>
                            </div>
                        ) : (
                            <>
                                <div className="flex gap-2">
                                    <TextInput
                                        value={zbcQuery}
                                        onChange={(e) => setZbcQuery(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") searchZbc(); }}
                                        placeholder="Search part number or name…"
                                        data-testid="sq-zbc-search-input"
                                    />
                                    <SlateButton onClick={searchZbc} disabled={zbcSearching} className="shrink-0">
                                        <Search className="h-4 w-4" /> {zbcSearching ? "…" : "Search"}
                                    </SlateButton>
                                </div>
                                {zbcResults.length > 0 && (
                                    <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-slate-200">
                                        {zbcResults.map((z) => (
                                            <button
                                                type="button"
                                                key={z.id}
                                                onClick={() => pickZbc(z)}
                                                className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center justify-between"
                                                data-testid={`sq-zbc-pick-${z.id}`}
                                            >
                                                <span><span className="font-mono font-semibold">{z.part_number}</span> — {z.part_name}</span>
                                                <span className="text-slate-500 font-mono">{fmtINR(z.should_cost || 0)}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Part Number" required>
                            <TextInput value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} disabled={!!linkedZbc} data-testid="sq-part-number" />
                        </Field>
                        <Field label="Part Name">
                            <TextInput value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })} disabled={!!linkedZbc} />
                        </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Quote Amount (₹)" required>
                            <NumberInput value={form.quote_amount} onChange={(e) => setForm({ ...form, quote_amount: e.target.value })} step="0.01" data-testid="sq-amount" />
                        </Field>
                        <Field label="Quoted Date" required>
                            <TextInput type="date" value={form.quoted_at} onChange={(e) => setForm({ ...form, quoted_at: e.target.value })} />
                        </Field>
                    </div>

                    <Field label="Valid Until (optional)">
                        <TextInput type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
                    </Field>

                    <Field label="Notes">
                        <TextInput value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Negotiation context, freight terms…" />
                    </Field>
                </div>
            </Modal>
        </div>
    );
}
